import React, { useEffect, useState } from "react";
import {
  usePlByProgramStyles,
  PlChartPanel,
} from "../pl-by-program/plByProgramTheme";
import Loading from "../../../../common-components/Loading";
import { getFundingExpenditureSummaryAPI } from "../../../../api/reports-api";
import { TH_ACTUAL_BG, TH_ACTUAL_TEXT } from "../../reportTableConfig";

const MONTH_KEYS = [
  "jan",
  "feb",
  "mar",
  "apr",
  "may",
  "jun",
  "jul",
  "aug",
  "sep",
  "oct",
  "nov",
  "dec",
];
const MONTH_LABEL_BY_KEY = {
  jan: "Jan",
  feb: "Feb",
  mar: "Mar",
  apr: "Apr",
  may: "May",
  jun: "Jun",
  jul: "Jul",
  aug: "Aug",
  sep: "Sep",
  oct: "Oct",
  nov: "Nov",
  dec: "Dec",
};
const MONTH_NUM_BY_KEY = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const thActualStyle = {
  background: TH_ACTUAL_BG,
  color: TH_ACTUAL_TEXT,
  fontWeight: 700,
};

const fmtN = (v) => {
  if (v == null) return "";
  const n = Math.round(Number(v));
  const abs = Math.abs(n).toLocaleString("en-US");
  return n < 0 ? `($${abs})` : `$${abs}`;
};
const fmtPct = (v) => (v == null ? "" : `${v.toFixed(1)}%`);

const LABEL_W = 140;
const COL_W = 110;

export default function SummaryTab({ classes, filters, onMeta }) {
  const plClasses = usePlByProgramStyles();
  const [loading, setLoading] = useState(true);
  const [itdRevenue, setItdRevenue] = useState(0);
  const [fundedValue, setFundedValue] = useState(0);
  const [months, setMonths] = useState({});
  const [columns, setColumns] = useState(MONTH_KEYS);
  const [fiscalYear, setFiscalYear] = useState(null);
  const [actualThru, setActualThru] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getFundingExpenditureSummaryAPI(filters)
      .then((res) => {
        if (cancelled) return;
        const d = res?.data;
        if (d) {
          /* API returns cent-level decimals; round to whole dollars for both the
             table and the chart labels, which are sized for integer amounts. */
          const roundedMonths = {};
          Object.entries(d.months ?? {}).forEach(([m, v]) => {
            roundedMonths[m] = {
              revenue: Math.round(v?.revenue ?? 0),
              cumRevenue: Math.round(v?.cumRevenue ?? 0),
              pctSpent: v?.pctSpent ?? 0,
            };
          });
          setItdRevenue(Math.round(d.itdRevenue ?? 0));
          setFundedValue(Math.round(d.fundedValue ?? 0));
          setMonths(roundedMonths);
          // Pruned to the allowed months by monthName/quarter filters — must
          // drive every month-column render below, not the full MONTH_KEYS.
          setColumns(
            Array.isArray(d.columns) && d.columns.length
              ? d.columns
              : MONTH_KEYS,
          );
          setFiscalYear(d.fiscalYear ?? null);
          setActualThru(Number(d.actualThroughMonth) || 0);
          onMeta?.({
            actualThroughMonth: d.actualThroughMonth,
            fiscalYear: d.fiscalYear,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading />;

  const REVENUE = columns.map((m) => months[m]?.revenue ?? 0);
  const CUM_REVENUE = columns.map((m) => months[m]?.cumRevenue ?? 0);
  const PCT_SPENT = columns.map((m) => months[m]?.pctSpent ?? 0);
  const COLUMN_LABELS = columns.map((m) => MONTH_LABEL_BY_KEY[m] || m);
  const yearLabel = fiscalYear ?? "";
  /* Scale the $ axis off both Contract Revenue and Cum Revenue, since Cum Revenue's
     bars need to fit on it too. Funded Value is a whole-of-contract ceiling routinely
     orders of magnitude larger than either — instead of squeezing it onto the $ axis,
     it's plotted on the % axis pinned at 100, i.e. shown as the ceiling line the other
     series are working toward, with the real dollar figure surfaced via its label. */
  const Y_MAX = Math.max(...REVENUE, ...CUM_REVENUE, 1) * 1.15;

  /* Revenue is typically in the tens of millions, not billions — a fixed
     "/1e9 bn" formatter rounds every tick down to "0.0bn". Pick the unit
     from the axis scale so ticks actually show distinct values. */
  const formatAxisValue = (v) => {
    const abs = Math.abs(v);
    if (abs >= 1e9) return `${(v / 1e9).toFixed(1)}bn`;
    if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
    if (abs >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
    return `${v}`;
  };

  const chartOption = {
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: {
      bottom: 4,
      itemGap: 18,
      data: [
        {
          name: "Contract Revenue",
          icon: "circle",
          itemStyle: { color: "#1a3a5c" },
        },
        {
          name: "Cum Revenue",
          icon: "circle",
          itemStyle: { color: "#E87722" },
        },
        { name: "% Spent", icon: "circle", itemStyle: { color: "#1E90FF" } },
        {
          name: "Funded Value",
          icon: "circle",
          itemStyle: { color: "#22C55E" },
        },
      ],
      textStyle: { fontSize: 11 },
    },
    grid: { top: 56, right: 76, bottom: 58, left: 96 },
    xAxis: {
      type: "category",
      data: COLUMN_LABELS,
      name: String(yearLabel),
      nameLocation: "center",
      nameGap: 28,
      nameTextStyle: { fontSize: 11, color: "#64748B" },
      axisLabel: { fontSize: 11 },
      axisTick: { show: false },
      axisLine: { lineStyle: { color: "#CBD5E1" } },
    },
    yAxis: [
      {
        type: "value",
        max: Y_MAX,
        name: "Contract Revenue",
        nameLocation: "middle",
        nameGap: 46,
        nameTextStyle: { fontSize: 11, color: "#64748B" },
        axisLabel: {
          formatter: formatAxisValue,
          fontSize: 10,
          color: "#64748B",
        },
        splitLine: { lineStyle: { type: "dashed", color: "#E9EDF3" } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      {
        type: "value",
        name: "% Spent",
        nameLocation: "middle",
        nameGap: 40,
        nameTextStyle: { fontSize: 11, color: "#64748B" },
        axisLabel: {
          formatter: (v) => `${v}%`,
          fontSize: 10,
          color: "#64748B",
        },
        max: 100,
        interval: 20,
        splitLine: { show: false },
        axisLine: { show: false },
        axisTick: { show: false },
      },
    ],
    series: [
      {
        name: "Contract Revenue",
        type: "bar",
        barGap: "10%",
        barMaxWidth: 24,
        data: REVENUE,
        itemStyle: { color: "#1a3a5c" },
        label: {
          show: true,
          position: "top",
          formatter: (p) => p.value.toLocaleString("en-US"),
          fontSize: 9,
          color: "#475569",
        },
      },
      {
        name: "Cum Revenue",
        type: "bar",
        barMaxWidth: 24,
        data: CUM_REVENUE,
        itemStyle: { color: "#E87722" },
        label: {
          show: true,
          position: "inside",
          rotate: 90,
          formatter: (p) => p.value.toLocaleString("en-US"),
          fontSize: 9,
          color: "#7C2D12",
          backgroundColor: "rgba(255,255,255,0.85)",
          borderRadius: 3,
          padding: [3, 2],
        },
      },
      {
        name: "% Spent",
        type: "line",
        yAxisIndex: 1,
        data: PCT_SPENT.map((v) => +v.toFixed(1)),
        symbol: "circle",
        symbolSize: 8,
        lineStyle: { color: "#1E90FF", width: 2, type: "dashed" },
        itemStyle: { color: "#1E90FF" },
        label: {
          show: true,
          formatter: (p) => `${p.value}%`,
          fontSize: 9,
          fontWeight: 600,
          color: "#1E4E8C",
          backgroundColor: "rgba(255,255,255,0.85)",
          borderRadius: 3,
          padding: [2, 4],
          position: "bottom",
          offset: [0, 4],
        },
      },
      {
        name: "Funded Value",
        type: "line",
        yAxisIndex: 1,
        data: columns.map(() => 100),
        symbol: "circle",
        symbolSize: 8,
        lineStyle: { color: "#22C55E", width: 2.5 },
        itemStyle: { color: "#22C55E" },
        label: {
          show: true,
          formatter: () => fundedValue.toLocaleString("en-US"),
          fontSize: 9,
          color: "#166534",
          position: "top",
          offset: [0, -4],
        },
        tooltip: {
          formatter: () =>
            `Funded Value<br/>${fundedValue.toLocaleString("en-US")}`,
        },
      },
    ],
  };

  return (
    <div>
      <div className={classes.tableCard}>
        <div className={classes.tableSectionHead}>
          <h2 className={classes.tableSectionTitle}>
            Revenue Vs Funded Value — FY{yearLabel}
          </h2>
        </div>
        <div className={classes.tableWrap}>
          <table
            className={classes.table}
            style={{
              width: LABEL_W + (columns.length + 1) * COL_W,
              minWidth: LABEL_W + (columns.length + 1) * COL_W,
            }}
          >
            <colgroup>
              <col style={{ width: LABEL_W }} />
              {Array(columns.length + 1)
                .fill(0)
                .map((_, i) => (
                  <col key={i} style={{ width: COL_W }} />
                ))}
            </colgroup>
            <thead>
              <tr>
                <th className={classes.thYear}>Year</th>
                <th className={classes.th2026} colSpan={columns.length + 1}>
                  {yearLabel}
                </th>
              </tr>
              <tr>
                <th className={classes.thSubLabel} />
                <th className={classes.thSub}>ITD</th>
                {columns.map((key) => {
                  const isActual = actualThru > 0 && MONTH_NUM_BY_KEY[key] <= actualThru;
                  return (
                    <th
                      key={key}
                      className={classes.thSub}
                      style={isActual ? thActualStyle : undefined}
                    >
                      {MONTH_LABEL_BY_KEY[key] || key}
                    </th>
                  );
                })}
              </tr>
            </thead>
            
            <tbody>
              <tr>
                <td className={classes.tdLabel}>Revenue</td>
                <td className={classes.tdVal}>{fmtN(itdRevenue)}</td>
                {REVENUE.map((v, i) => (
                  <td key={i} className={classes.tdVal}>
                    {fmtN(v)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={classes.tdLabel}>Cum Revenue</td>
                <td className={classes.tdVal} />
                {CUM_REVENUE.map((v, i) => (
                  <td key={i} className={classes.tdVal}>
                    {fmtN(v)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={classes.tdLabel}>Funded Value</td>
                <td className={classes.tdVal} />
                {columns.map((key) => (
                  <td key={key} className={classes.tdVal}>
                    {fmtN(fundedValue)}
                  </td>
                ))}
              </tr>
              <tr>
                <td className={classes.tdLabel}>% Spent</td>
                <td className={classes.tdPct} />
                {PCT_SPENT.map((v, i) => (
                  <td key={i} className={classes.tdPct}>
                    {fmtPct(v)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <PlChartPanel
        title={`Funding Expenditure Report`}
        option={chartOption}
        classes={plClasses}
        panelClassName={plClasses.chartPanelLg}
      />
    </div>
  );
}
